/**
 * Registration Page - Split-Screen Dark Theme
 * Left: Branding + Benefits | Right: Registration Form
 */
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, User, Mail, Lock, ArrowLeft, Phone, Eye, EyeOff, Check, X, Terminal, Cloud, Shield, Award, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Turnstile } from '@marsidev/react-turnstile';

// Cloudflare's "always-pass" test sitekey — used when env var not configured
// so dev/staging continues to work even before the real key is provisioned.
const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA';
const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || TURNSTILE_TEST_SITEKEY;

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    countryCode: '+91',
    customCountryCode: '',
    mobileNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    noWeakPatterns: false
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);

  // ── Anti-abuse fields (kept invisible / hidden from user)
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const [honeypot, setHoneypot] = useState('');  // hidden — bots auto-fill, humans don't
  const formStartedAtRef = useRef(Date.now());   // captured on mount; sent to backend
  const turnstileRef = useRef(null);

  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const countryCodes = [
    { code: '+91', country: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
    { code: '+1', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
    { code: '+44', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
    { code: '+971', country: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
    { code: '+65', country: 'Singapore', flag: '\u{1F1F8}\u{1F1EC}' },
    { code: '+86', country: 'China', flag: '\u{1F1E8}\u{1F1F3}' },
    { code: '+81', country: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
    { code: '+82', country: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
    { code: '+61', country: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
    { code: '+64', country: 'New Zealand', flag: '\u{1F1F3}\u{1F1FF}' },
    { code: '+66', country: 'Thailand', flag: '\u{1F1F9}\u{1F1ED}' },
    { code: '+60', country: 'Malaysia', flag: '\u{1F1F2}\u{1F1FE}' },
    { code: '+62', country: 'Indonesia', flag: '\u{1F1EE}\u{1F1E9}' },
    { code: '+63', country: 'Philippines', flag: '\u{1F1F5}\u{1F1ED}' },
    { code: '+84', country: 'Vietnam', flag: '\u{1F1FB}\u{1F1F3}' },
    { code: '+92', country: 'Pakistan', flag: '\u{1F1F5}\u{1F1F0}' },
    { code: '+880', country: 'Bangladesh', flag: '\u{1F1E7}\u{1F1E9}' },
    { code: '+94', country: 'Sri Lanka', flag: '\u{1F1F1}\u{1F1F0}' },
    { code: '+977', country: 'Nepal', flag: '\u{1F1F3}\u{1F1F5}' },
    { code: '+966', country: 'Saudi Arabia', flag: '\u{1F1F8}\u{1F1E6}' },
    { code: '+974', country: 'Qatar', flag: '\u{1F1F6}\u{1F1E6}' },
    { code: '+965', country: 'Kuwait', flag: '\u{1F1F0}\u{1F1FC}' },
    { code: '+973', country: 'Bahrain', flag: '\u{1F1E7}\u{1F1ED}' },
    { code: '+968', country: 'Oman', flag: '\u{1F1F4}\u{1F1F2}' },
    { code: '+972', country: 'Israel', flag: '\u{1F1EE}\u{1F1F1}' },
    { code: '+90', country: 'Turkey', flag: '\u{1F1F9}\u{1F1F7}' },
    { code: '+98', country: 'Iran', flag: '\u{1F1EE}\u{1F1F7}' },
    { code: '+49', country: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
    { code: '+33', country: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
    { code: '+39', country: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
    { code: '+34', country: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
    { code: '+31', country: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}' },
    { code: '+41', country: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
    { code: '+46', country: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
    { code: '+47', country: 'Norway', flag: '\u{1F1F3}\u{1F1F4}' },
    { code: '+45', country: 'Denmark', flag: '\u{1F1E9}\u{1F1F0}' },
    { code: '+358', country: 'Finland', flag: '\u{1F1EB}\u{1F1EE}' },
    { code: '+7', country: 'Russia', flag: '\u{1F1F7}\u{1F1FA}' },
    { code: '+48', country: 'Poland', flag: '\u{1F1F5}\u{1F1F1}' },
    { code: '+420', country: 'Czech Republic', flag: '\u{1F1E8}\u{1F1FF}' },
    { code: '+43', country: 'Austria', flag: '\u{1F1E6}\u{1F1F9}' },
    { code: '+32', country: 'Belgium', flag: '\u{1F1E7}\u{1F1EA}' },
    { code: '+1', country: 'Canada', flag: '\u{1F1E8}\u{1F1E6}' },
    { code: '+52', country: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
    { code: '+55', country: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
    { code: '+54', country: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
    { code: '+56', country: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
    { code: '+57', country: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}' },
    { code: '+51', country: 'Peru', flag: '\u{1F1F5}\u{1F1EA}' },
    { code: '+27', country: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
    { code: '+234', country: 'Nigeria', flag: '\u{1F1F3}\u{1F1EC}' },
    { code: '+20', country: 'Egypt', flag: '\u{1F1EA}\u{1F1EC}' },
    { code: '+254', country: 'Kenya', flag: '\u{1F1F0}\u{1F1EA}' },
    { code: '+233', country: 'Ghana', flag: '\u{1F1EC}\u{1F1ED}' },
    { code: '+380', country: 'Ukraine', flag: '\u{1F1FA}\u{1F1E6}' },
    { code: '+375', country: 'Belarus', flag: '\u{1F1E7}\u{1F1FE}' },
    { code: '+995', country: 'Georgia', flag: '\u{1F1EC}\u{1F1EA}' },
    { code: 'other', country: 'Other Country', flag: '\u{1F30D}' }
  ];

  const checkEmailAvailability = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setEmailCheckLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmailAvailable(data.available);
        if (!data.available) {
          setFieldErrors(prev => ({ ...prev, email: 'This email is already registered.' }));
        } else {
          setFieldErrors(prev => { const e = { ...prev }; delete e.email; return e; });
        }
      }
    } catch (err) {
      console.error('Email check failed:', err);
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const checkPasswordStrength = (password) => {
    const weakPatterns = ['password', '123456', 'qwerty', 'admin', 'login'];
    const hasWeakPattern = weakPatterns.some(p => password.toLowerCase().includes(p));
    const requirements = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      noWeakPatterns: !hasWeakPattern
    };
    setPasswordRequirements(requirements);
    const met = Object.values(requirements).filter(Boolean).length;
    if (password.length === 0) setPasswordStrength(null);
    else if (met < 3) setPasswordStrength({ level: 'weak', color: 'text-rose-500', bg: 'bg-rose-500', width: '33%' });
    else if (met < 6) setPasswordStrength({ level: 'good', color: 'text-amber-500', bg: 'bg-amber-500', width: '66%' });
    else setPasswordStrength({ level: 'strong', color: 'text-emerald-500', bg: 'bg-emerald-500', width: '100%' });
  };

  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    switch (name) {
      case 'first_name':
        if (!value.trim()) errors.first_name = 'First name is required';
        else if (value.length < 2) errors.first_name = 'Min 2 characters';
        else if (value.includes(' ')) errors.first_name = 'Please enter first name only';
        else delete errors.first_name;
        break;
      case 'last_name':
        if (!value.trim()) errors.last_name = 'Last name is required';
        else if (value.length < 2) errors.last_name = 'Min 2 characters';
        else delete errors.last_name;
        break;
      case 'email':
        if (!value) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors.email = 'Invalid email';
        else delete errors.email;
        break;
      case 'mobileNumber':
        if (!value) errors.mobileNumber = 'Mobile number is required';
        else if (value.length < 10) errors.mobileNumber = 'Min 10 digits';
        else if (!/^\d+$/.test(value)) errors.mobileNumber = 'Digits only';
        else delete errors.mobileNumber;
        break;
      case 'confirmPassword':
        if (!value) errors.confirmPassword = 'Please confirm password';
        else if (value !== formData.password) errors.confirmPassword = 'Passwords do not match';
        else delete errors.confirmPassword;
        break;
      default: break;
    }
    setFieldErrors(errors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    validateField(name, value);
    if (name === 'password') checkPasswordStrength(value);
    if (name === 'confirmPassword' || name === 'password') {
      validateField('confirmPassword', name === 'confirmPassword' ? value : formData.confirmPassword);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    Object.keys(formData).forEach(key => {
      if (key !== 'countryCode' && key !== 'customCountryCode') validateField(key, formData[key]);
    });
    if (Object.keys(fieldErrors).length > 0) { setError('Please fix all errors'); return; }
    if (!passwordStrength || passwordStrength.level !== 'strong') {
      setError('Password must meet ALL security requirements.');
      return;
    }
    try {
      const { confirmPassword, ...userData } = formData;
      const finalCountryCode = formData.countryCode === 'other' ? formData.customCountryCode : formData.countryCode;
      userData.mobile_number = finalCountryCode + formData.mobileNumber;
      delete userData.countryCode;
      delete userData.customCountryCode;
      delete userData.mobileNumber;
      // Anti-abuse payload — honeypot (must stay empty), form-start ms,
      // Cloudflare Turnstile token. All optional on backend so missing values
      // still work in dev when env var isn't configured.
      userData.website = honeypot;
      userData.form_started_at = formStartedAtRef.current;
      userData.turnstile_token = turnstileToken;
      await register(userData);
      toast.success('Welcome to Grras Solutions! Account created. Please login.', {
        duration: 5000, position: 'top-center',
        style: { background: '#10b981', color: 'white', fontWeight: '600', borderRadius: '12px' }
      });
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      let errorMessage = 'Registration failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' ? error.response.data.detail : error.response.data.detail.message || errorMessage;
      } else if (error.message) errorMessage = error.message;
      if (errorMessage.includes('already registered') || errorMessage.includes('DUPLICATE') || errorMessage.includes('Email already registered')) {
        setError('This email is already registered.');
        setFieldErrors(prev => ({ ...prev, email: 'Email already exists' }));
        toast.error('Email already registered! Try logging in.');
      } else if (errorMessage.includes('mobile') || errorMessage.includes('phone')) {
        setError('This mobile number is already registered.');
        setFieldErrors(prev => ({ ...prev, mobileNumber: 'Number already exists' }));
      } else if (errorMessage.toLowerCase().includes('security check') || errorMessage.toLowerCase().includes('verification')) {
        setError('Security check failed. Please wait a moment and try again.');
        toast.error('Verification failed — please try again.');
        try { turnstileRef.current?.reset(); } catch (_) {}
        setTurnstileToken('');
      } else if (errorMessage.toLowerCase().includes('too many') || errorMessage.toLowerCase().includes('rate')) {
        setError('Too many signup attempts from your network. Please try again in an hour.');
        toast.error('Rate limit exceeded.');
      } else if (errorMessage.toLowerCase().includes('disposable') || errorMessage.toLowerCase().includes('temporary / disposable')) {
        setError('Please use a permanent email address (not a temporary inbox).');
        setFieldErrors(prev => ({ ...prev, email: 'Use a permanent email address' }));
      } else if (errorMessage.toLowerCase().includes('too quickly') || errorMessage.toLowerCase().includes('invalid form')) {
        setError('Form submission looked automated. Please refresh the page and try again.');
      } else {
        setError(errorMessage);
        toast.error('Registration failed.');
      }
      // Reset Turnstile so the user can retry without a stale token
      try { turnstileRef.current?.reset(); } catch (_) {}
      setTurnstileToken('');
    }
  };

  const benefits = [
    { icon: Terminal, title: 'Linux & DevOps', desc: 'RHCSA, Docker, Kubernetes, Ansible, Terraform' },
    { icon: BookOpen, title: 'Programming', desc: 'Python, C, C++, Core Java — Zero to Hero' },
    { icon: Cloud, title: 'Database & Cloud', desc: 'MySQL/MariaDB, Infrastructure as Code' },
    { icon: Shield, title: 'Certification Ready', desc: 'RHCSA, RHCE, DO188 exam prep — 95% pass rate' },
  ];

  const RequirementItem = ({ met, text }) => (
    <div className={`flex items-center gap-1.5 transition-colors duration-200 ${met ? 'text-emerald-500' : 'text-slate-500'}`}>
      {met ? <Check className="h-3 w-3 flex-shrink-0" /> : <X className="h-3 w-3 flex-shrink-0" />}
      <span className="text-xs">{text}</span>
    </div>
  );

  const inputClasses = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 h-11";

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col lg:flex-row" data-testid="register-page">

      {/* ─── Left Panel: Branding ─── */}
      <div className="hidden lg:flex lg:w-[40%] xl:w-[38%] relative flex-col justify-between overflow-hidden sticky top-0 h-screen">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3NTc0MzU2NXww&ixlib=rb-4.1.0&q=85"
            alt="" className="w-full h-full object-cover" aria-hidden="true"
          />
          <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-sm" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-14">
          {/* Top: Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-12 group">
              <img src="/images/grras-logo.png" alt="Grras Solutions" className="h-16 md:h-20 w-auto" />
            </Link>

            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-md px-3 py-1.5 rounded-full mb-6">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg" alt="Red Hat" className="h-4 w-4" />
              <span className="text-xs text-slate-300 font-medium">Official Red Hat Training Partner</span>
            </div>

            <h1 className="text-3xl xl:text-4xl font-bold text-white tracking-tight leading-tight mb-4">
              Learn.<br />
              <span className="text-indigo-400">Build. Certify.</span>
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              250+ hands-on labs across Linux, DevOps, Programming & Databases — on real VMs with auto-evaluation.
            </p>
          </div>

          {/* Middle: Benefits */}
          <div className="space-y-4 my-8">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3.5 group">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-600/20 group-hover:border-indigo-500/30 transition-all duration-300">
                  <b.icon className="h-4 w-4 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-200">{b.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom: Trust */}
          <div className="flex items-center gap-3 flex-wrap">
            {[
              { icon: Shield, label: 'Secure' },
              { icon: Award, label: 'ISO Certified' },
            ].map((badge, i) => (
              <div key={i} className="bg-white/5 border border-white/10 backdrop-blur-md px-3.5 py-2 rounded-full flex items-center gap-2 text-xs text-slate-300">
                <badge.icon className="h-3.5 w-3.5 text-indigo-400" />
                {badge.label}
              </div>
            ))}
            <div className="text-xs text-slate-500 ml-1">10,000+ students trained</div>
          </div>
        </div>
      </div>

      {/* ─── Right Panel: Form ─── */}
      <div className="flex-1 flex items-start lg:items-center justify-center overflow-y-auto p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">

          {/* Mobile Brand Header */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <img src="/images/grras-logo.png" alt="Grras Solutions" className="h-10 md:h-12 w-auto" />
            </Link>
            <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg" alt="Red Hat" className="h-3.5 w-3.5" />
              <span className="text-[10px] text-slate-400">Red Hat Training Partner</span>
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white tracking-tight">Create Account</h2>
            <p className="text-slate-400 text-sm mt-1.5">Fill in your details to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {error && (
              <Alert className="bg-rose-500/10 border-rose-500/20" data-testid="register-error">
                <AlertDescription className="text-rose-400 text-sm">{error}</AlertDescription>
              </Alert>
            )}

            {/* Name Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">First Name</Label>
                <Input id="first_name" name="first_name" data-testid="register-first-name" value={formData.first_name} onChange={handleChange}
                  className={`mt-1.5 ${inputClasses} ${fieldErrors.first_name ? 'border-rose-500/50' : ''}`}
                  placeholder="Enter first name only (e.g. Ravi)" required aria-invalid={!!fieldErrors.first_name}
                />
                {fieldErrors.first_name
                  ? <p className="text-rose-400 text-xs mt-1">{fieldErrors.first_name}</p>
                  : <p className="text-slate-500 text-xs mt-1">Please enter your first name only, not full name</p>
                }
              </div>
              <div>
                <Label htmlFor="last_name" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Last Name</Label>
                <Input id="last_name" name="last_name" data-testid="register-last-name" value={formData.last_name} onChange={handleChange}
                  className={`mt-1.5 ${inputClasses} ${fieldErrors.last_name ? 'border-rose-500/50' : ''}`}
                  placeholder="Doe" required aria-invalid={!!fieldErrors.last_name}
                />
                {fieldErrors.last_name && <p className="text-rose-400 text-xs mt-1">{fieldErrors.last_name}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Email Address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input id="email" name="email" type="email" data-testid="register-email" value={formData.email} onChange={handleChange}
                  onBlur={(e) => checkEmailAvailability(e.target.value)}
                  className={`pl-10 pr-10 ${inputClasses} ${fieldErrors.email ? 'border-rose-500/50' : emailAvailable === true ? 'border-emerald-500/50' : ''}`}
                  placeholder="john@example.com" required aria-invalid={!!fieldErrors.email}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {emailCheckLoading ? <Loader2 className="h-4 w-4 text-slate-500 animate-spin" />
                    : emailAvailable === true ? <Check className="h-4 w-4 text-emerald-500" />
                    : emailAvailable === false ? <X className="h-4 w-4 text-rose-500" /> : null}
                </div>
              </div>
              {fieldErrors.email && <p className="text-rose-400 text-xs mt-1">{fieldErrors.email}</p>}
              {emailAvailable === true && !fieldErrors.email && <p className="text-emerald-500 text-xs mt-1">Email available</p>}
            </div>

            {/* Mobile */}
            <div>
              <Label htmlFor="mobileNumber" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Mobile Number</Label>
              <div className="mt-1.5 flex gap-2">
                {formData.countryCode === 'other' ? (
                  <Input name="customCountryCode" data-testid="register-custom-country" value={formData.customCountryCode} onChange={handleChange}
                    className={`w-20 ${inputClasses}`} placeholder="+XX"
                  />
                ) : (
                  <select name="countryCode" data-testid="register-country-code" value={formData.countryCode} onChange={handleChange}
                    className="px-2.5 py-2 bg-slate-900 border border-slate-700 rounded-md text-white text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none max-w-[120px] h-11 cursor-pointer"
                  >
                    {countryCodes.map((c, i) => (
                      <option key={`${c.code}-${i}`} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                )}
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input id="mobileNumber" name="mobileNumber" type="tel" data-testid="register-mobile" value={formData.mobileNumber} onChange={handleChange}
                    className={`pl-10 ${inputClasses} ${fieldErrors.mobileNumber ? 'border-rose-500/50' : ''}`}
                    placeholder="9876543210" required aria-invalid={!!fieldErrors.mobileNumber}
                  />
                </div>
              </div>
              {formData.countryCode === 'other' && <p className="text-xs text-indigo-400 mt-1">Enter your country code manually</p>}
              {fieldErrors.mobileNumber && <p className="text-rose-400 text-xs mt-1">{fieldErrors.mobileNumber}</p>}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input id="password" name="password" data-testid="register-password" type={showPassword ? "text" : "password"}
                  value={formData.password} onChange={handleChange}
                  className={`pl-10 pr-10 ${inputClasses}`}
                  placeholder="Create a strong password" required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors" data-testid="toggle-password">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Strength Bar */}
              {formData.password && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-slate-500">Strength</span>
                    {passwordStrength && <span className={`text-xs font-medium capitalize ${passwordStrength.color}`}>{passwordStrength.level}</span>}
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full transition-all duration-500 ${passwordStrength?.bg || ''}`} style={{ width: passwordStrength?.width || '0%' }} />
                  </div>
                </div>
              )}

              {/* Requirements Grid */}
              {formData.password && (
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <RequirementItem met={passwordRequirements.length} text="12+ characters" />
                  <RequirementItem met={passwordRequirements.uppercase} text="Uppercase letter" />
                  <RequirementItem met={passwordRequirements.lowercase} text="Lowercase letter" />
                  <RequirementItem met={passwordRequirements.number} text="Number" />
                  <RequirementItem met={passwordRequirements.special} text="Special character" />
                  <RequirementItem met={passwordRequirements.noWeakPatterns} text="No weak patterns" />
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Confirm Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input id="confirmPassword" name="confirmPassword" data-testid="register-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword} onChange={handleChange}
                  className={`pl-10 pr-10 ${inputClasses} ${fieldErrors.confirmPassword ? 'border-rose-500/50' : formData.confirmPassword && formData.confirmPassword === formData.password ? 'border-emerald-500/50' : ''}`}
                  placeholder="Confirm your password" required aria-invalid={!!fieldErrors.confirmPassword}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors" data-testid="toggle-confirm-password">
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldErrors.confirmPassword && <p className="text-rose-400 text-xs mt-1">{fieldErrors.confirmPassword}</p>}
              {formData.confirmPassword && !fieldErrors.confirmPassword && formData.confirmPassword === formData.password && (
                <p className="text-emerald-500 text-xs mt-1 flex items-center gap-1"><Check className="h-3 w-3" />Passwords match</p>
              )}
            </div>

            {/* ── Anti-abuse layer (Layer 3 — honeypot, Layer 1 — Turnstile invisible) ── */}
            {/* Honeypot: real users never see/touch this. Bots auto-fill every named input.
                Hidden via inline styles (off-screen + aria-hidden + tabindex=-1).
                Backend rejects the submit if this is non-empty. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', height: 0, width: 0, overflow: 'hidden' }}>
              <label>
                Website (leave blank)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
            </div>

            {/* Cloudflare Turnstile — invisible mode. Generates a token in the background;
                we attach it to the form payload at submit time. */}
            <div className="flex justify-center" data-testid="register-turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITEKEY}
                onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false); }}
                onError={() => { setTurnstileError(true); setTurnstileToken(''); }}
                onExpire={() => { setTurnstileToken(''); try { turnstileRef.current?.reset(); } catch (_) {} }}
                options={{ theme: 'dark', appearance: 'interaction-only' }}
              />
            </div>
            {turnstileError && (
              <p className="text-xs text-rose-400 -mt-2 text-center">Security check unavailable — please refresh and try again.</p>
            )}

            {/* Submit */}
            <Button type="submit" data-testid="register-submit-button"
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-all duration-200 shadow-[0_0_15px_rgba(79,70,229,0.15)] hover:shadow-[0_0_25px_rgba(79,70,229,0.3)] hover:-translate-y-0.5"
              disabled={loading || Object.keys(fieldErrors).length > 0}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Account
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <span className="text-slate-500 text-sm">Already have an account? </span>
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium text-sm transition-colors" data-testid="login-link">
              Sign In
            </Link>
          </div>

          {/* Back to Home - Mobile */}
          <div className="mt-4 text-center lg:hidden">
            <Link to="/" className="text-slate-500 hover:text-slate-300 text-xs inline-flex items-center gap-1 transition-colors">
              <ArrowLeft className="h-3 w-3" /> Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
